import { FileDown, Share2 } from "lucide-react";
import { invoicePdfUrl, invoiceToWhatsappText, shareInvoicePdf, canShareFiles } from "@/lib/api";

export default function InvoiceCard({ invoice }) {
  const shareOnWhatsapp = async () => {
    if (canShareFiles()) {
      const shared = await shareInvoicePdf(invoice);
      if (shared) return;
    }
    const text = encodeURIComponent(invoiceToWhatsappText(invoice));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <div data-testid="invoice-card" className="bg-white rounded-lg border-2 border-dashed border-[#006D5B]/30 p-3 mt-1 min-w-[220px]">
      {invoice.store_name && (
        <p data-testid="invoice-card-store" className="font-heading font-bold text-[12px] text-[#006D5B] leading-tight mb-1">
          🏪 {invoice.store_name}
          {invoice.gstin && <span className="block text-[9px] text-gray-400 font-mono font-normal">GSTIN: {invoice.gstin}</span>}
        </p>
      )}
      <div className="flex justify-between items-baseline border-b border-dotted border-gray-300 pb-1.5 mb-1.5">
        <span className="font-heading font-bold text-[#006D5B] text-sm">
          {invoice.invoice_no}
          {invoice.payment_status === "udhaar" && (
            <span data-testid="invoice-udhaar-badge" className="ml-1.5 text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold align-middle">UDHAAR</span>
          )}
        </span>
        <span className="text-[10px] text-gray-500">{invoice.date}</span>
      </div>
      <p className="text-xs font-semibold mb-1.5">👤 {invoice.customer}</p>
      <table className="w-full text-[11px] font-mono">
        <tbody>
          {(invoice.items || []).map((it, i) => (
            <tr key={`${it.name}-${i}`} className="align-top">
              <td className="pr-1 py-0.5">{it.name}</td>
              <td className="text-right whitespace-nowrap text-gray-500">
                {it.qty}{it.unit ? ` ${it.unit}` : ""} × ₹{it.rate}
              </td>
              <td className="text-right pl-2 font-semibold">₹{Number(it.amount).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-dotted border-gray-300 mt-1.5 pt-1.5 text-[11px] font-mono space-y-0.5">
        {(invoice.gst_enabled !== false || Number(invoice.discount || 0) > 0) && (
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span><span>₹{Number(invoice.subtotal).toFixed(2)}</span>
          </div>
        )}
        {Number(invoice.discount || 0) > 0 && (
          <div data-testid="invoice-discount-row" className="flex justify-between text-emerald-700">
            <span>Discount{invoice.discount_percent ? ` (${invoice.discount_percent}%)` : ""}</span>
            <span>-₹{Number(invoice.discount).toFixed(2)}</span>
          </div>
        )}
        {invoice.gst_enabled !== false && (
          <div data-testid="invoice-gst-row" className="flex justify-between text-gray-600">
            <span>GST</span><span>₹{Number(invoice.gst_amount).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-sm text-[#006D5B] pt-0.5">
          <span>TOTAL</span><span data-testid="invoice-total">₹{Number(invoice.total).toFixed(2)}</span>
        </div>
      </div>
      <div className="flex gap-2 mt-2.5 pt-2 border-t border-dotted border-gray-300">
        <button
          data-testid="invoice-share-whatsapp-btn"
          onClick={shareOnWhatsapp}
          className="flex-1 flex items-center justify-center gap-1.5 bg-[#006D5B] text-white text-[11px] font-semibold py-1.5 rounded-full hover:opacity-90 transition-opacity duration-150"
        >
          <Share2 size={12} /> WhatsApp
        </button>
        {invoice.id && (
          <a
            data-testid="invoice-pdf-btn"
            href={invoicePdfUrl(invoice.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 border border-[#006D5B] text-[#006D5B] text-[11px] font-semibold py-1.5 rounded-full hover:bg-[#006D5B]/5 transition-colors duration-150"
          >
            <FileDown size={12} /> PDF
          </a>
        )}
      </div>
    </div>
  );
}
